(() => {
  if (customElements.get('wat-shop-feedback')) return;
  class ShopFeedback extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.shown = false;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.status = this.querySelector('[role="status"]');
      this.key = `wat-feedback:${this.dataset.campaign}`;
      this.preview = this.isTestMode();
      this.abort = new AbortController();
      const on = (el, event, callback) => el.addEventListener(event, callback, {signal:this.abort.signal});
      this.read = () => { try { return JSON.parse(localStorage.getItem(this.key)) || this.state || {}; } catch { return this.state || {}; } };
      this.save = state => { this.state = state; try { localStorage.setItem(this.key, JSON.stringify(state)); } catch {} };
      this.state = this.read();
      this.windowMs = Number(this.dataset.window) * 60000;
      this.lastActivity = Date.now();
      this.lastTick = Date.now();
      // Navigation timing keeps the delay tied to this page, including editor reloads.
      const navigation = performance.getEntriesByType('navigation')[0];
      this.readyAt = navigation?.loadEventEnd > 0 ? performance.timeOrigin + navigation.loadEventEnd + 6000 : Infinity;
      if (document.readyState === 'complete' && this.readyAt === Infinity) this.readyAt = Date.now() + 6000;
      on(window, 'load', () => { this.readyAt = Date.now() + 6000; });
      for (const event of ['pointerdown','pointermove','keydown','scroll','touchstart']) on(document,event,()=>{this.lastActivity=Date.now();});
      on(document,'visibilitychange',()=>{this.lastTick=Date.now();});
      on(this.querySelector('[data-close]'),'click',()=>{
        this.resetOnClose = true;
        this.dialog.close();
      });
      on(this.dialog,'click',event=>{if(event.target===this.dialog)this.dialog.close();});
      on(this.querySelector('[data-dismiss]'),'click',()=>{
        if (!this.preview) this.save({...this.state, done:true});
        this.dialog.close();
      });
      on(this.dialog,'close',()=>{
        document.documentElement.style.overflow=this.savedOverflow;
        this.pausedLenis?.start?.();
        this.previousFocus?.focus?.({preventScroll:true});
        if (this.resetOnClose) {
          this.resetOnClose = false;
          this.resetCounters();
        } else if (!this.preview && !this.state.done) {
          this.save({...this.state, until:Date.now()+Number(this.dataset.snooze)*86400000});
        }
      });
      on(this.form.comment,'input',()=>{this.querySelector('.wat-feedback-count').textContent=`${this.form.comment.value.length} / 500`;});
      on(this.form,'submit',event=>this.submit(event));
      on(window,'storage',event=>{if(event.key===this.key){this.state=this.read();if(this.state.done&&this.dialog.open)this.dialog.close();}});
      if (!this.preview && (!this.dataset.endpoint || !this.dataset.campaign)) return;
      this.tick(true);
      this.timer=setInterval(()=>this.tick(false),1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      if(this.dialog.open)this.dialog.close();
      this.abort.abort();
      this.initialized=false;
    }
    tick(first) {
      const now=Date.now();
      if(this.preview && this.hasAttribute('data-test')){if(!this.shown)this.open();return;}
      const stored=this.read();
      if(stored.done||stored.until>now){this.lastTick=now;return;}
      let state=stored;
      if(!state.start||now-state.start>this.windowMs)state={start:now,active:0,pages:[]};
      if(!Array.isArray(state.pages))state.pages=[];
      if(first||this.path!==location.pathname){this.path=location.pathname;if(!state.pages.includes(this.path))state.pages.push(this.path);}
      if(document.visibilityState==='visible'&&document.hasFocus()&&now-this.lastActivity<60000){state.active=(Number(state.active)||0)+Math.min(1500,now-this.lastTick);}
      this.lastTick=now;
      this.save(state);
      if(now >= this.readyAt && !this.shown && (state.active>=Number(this.dataset.minutes)*60000 || state.pages.length>=Number(this.dataset.pages)))this.open();
    }
    isTestMode() {
      return this.hasAttribute('data-preview') || window.Shopify?.designMode === true ||
        (this.hasAttribute('data-test') && window.Shopify?.previewMode === true);
    }
    resetCounters() {
      if (this.state.done) return;
      const now = Date.now();
      this.lastActivity = now;
      this.lastTick = now;
      // Do not count the currently open page again; only subsequent pages count.
      this.path = location.pathname;
      this.save({start:now, active:0, pages:[]});
      if (!(this.preview && this.hasAttribute('data-test'))) this.shown = false;
    }
    hasBlockingOverlay() {
      return [...document.querySelectorAll('dialog[open], [aria-modal="true"], .drawer[active], .drawer[open]')].some(element => {
        if (element === this.dialog || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
        if (element.matches('dialog')) return element.open;
        if (element.classList.contains('drawer')) return element.hasAttribute('active') || element.hasAttribute('open') || element.getAttribute('aria-hidden') === 'false';
        const style = getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && element.getClientRects().length > 0;
      });
    }
    open() {
      if(document.visibilityState!=='visible')return;
      if(!(this.preview && this.hasAttribute('data-test')) && (this.hasBlockingOverlay()||document.documentElement.style.overflow==='hidden'||document.body.style.overflow==='hidden'))return;
      this.shown=true;
      this.previousFocus=document.activeElement;
      this.savedOverflow=document.documentElement.style.overflow;
      this.pausedLenis=window.lenis&&!window.lenis.isStopped?window.lenis:null;
      this.pausedLenis?.stop?.();
      document.documentElement.style.overflow='hidden';
      this.dialog.showModal();
      this.querySelector('[data-initial-focus]')?.focus({preventScroll:true});
    }
    async submit(event) {
      event.preventDefault();
      if(!this.form.reportValidity())return;
      if(this.preview){this.status.textContent='Vorschau: Es wird kein Feedback gespeichert.';return;}
      try { if(new URL(this.dataset.endpoint).protocol!=='https:')throw new Error(); }
      catch { this.status.textContent='Feedback kann gerade nicht gesendet werden. Bitte später erneut versuchen.'; console.error('Shop-Feedback: Bitte einen gültigen HTTPS-Endpunkt im Theme konfigurieren.'); return; }
      const button=this.querySelector('[type="submit"]');
      if(button.disabled)return;
      button.disabled=true;
      this.status.textContent='Wird gesendet …';
      try {
        const response=await fetch(this.dataset.endpoint,{method:'POST',credentials:'omit',headers:{'Content-Type':'application/json'},signal:AbortSignal.timeout(15000),body:JSON.stringify({campaign:this.dataset.campaign,rating:this.form.rating.value,comment:this.form.comment.value})});
        if(!response.ok)throw new Error();
        const result=await response.json();
        if(result.ok!==true)throw new Error();
        this.save({...this.state,done:true});
        this.form.hidden=true;
        this.querySelector('.wat-feedback-footer').hidden=true;
        this.status.textContent='Danke dir! ❤️';
      } catch {this.status.textContent='Das hat leider nicht geklappt. Bitte versuche es noch einmal.';}
      finally {button.disabled=false;}
    }
  }
  customElements.define('wat-shop-feedback',ShopFeedback);
})();
